'use client';

import { useState, useEffect } from 'react';
import { Container } from '@/components/ui/Container';
import Link from 'next/link';
import { useSession } from 'next-auth/react';

export default function EventsPage() {
  const { data: session } = useSession();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/user/events');
      
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      
      const data = await response.json();
      setEvents(data.events);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Container>
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Upcoming Events</h1>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
            </div>
          ) : error ? (
            <div className="text-center text-red-600 py-8">{error}</div>
          ) : events.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No upcoming events at the moment.</div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {events.map((event) => (
                <div 
                  key={event.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{event.name}</h3>
                    <div className="space-y-3 text-gray-600">
                      <p className="flex items-center">
                        <svg className="h-5 w-5 mr-2 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {event.location}
                      </p>
                      <p className="flex items-center">
                        <svg className="h-5 w-5 mr-2 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(event.date)}
                      </p>
                    </div>

                    <div className="mt-4 space-y-2 text-sm">
                      {event.conferencePrice > 0 && (
                        <p className="flex justify-between">
                          <span>Conference:</span>
                          <span className="font-medium">{formatPrice(event.conferencePrice)}</span>
                        </p>
                      )}
                      {event.workshopPrice > 0 && (
                        <p className="flex justify-between">
                          <span>Workshop:</span>
                          <span className="font-medium">{formatPrice(event.workshopPrice)}</span>
                        </p>
                      )}
                      {event.passportPrice > 0 && (
                        <p className="flex justify-between">
                          <span>Passport:</span>
                          <span className="font-medium">{formatPrice(event.passportPrice)}</span>
                        </p>
                      )}
                      {event.conferencePrice === 0 && event.workshopPrice === 0 && event.passportPrice === 0 && (
                        <p className="text-center text-[#10B981] font-medium">Free Event</p>
                      )}
                    </div>

                    <div className="mt-6">
                      {event.registrationOpen ? (
                        session ? (
                          event.isRegistered ? (
                            <Link
                              href="/user/dashboard"
                              className="block w-full text-center px-4 py-2 bg-[#10B981] text-white rounded-md hover:bg-[#3B82F6] transition-colors"
                            >
                              View Dashboard
                            </Link>
                          ) : (
                            <Link
                              href={`/events/${event.id}`}
                              className="block w-full text-center px-4 py-2 bg-[#10B981] text-white rounded-md hover:bg-[#3B82F6] transition-colors"
                            >
                              Register Now
                            </Link>
                          )
                        ) : (
                          <Link
                            href="/auth/signin"
                            className="block w-full text-center px-4 py-2 bg-[#10B981] text-white rounded-md hover:bg-[#3B82F6] transition-colors"
                          >
                            Register Now
                          </Link>
                        )
                      ) : (
                        <button
                          disabled
                          className="block w-full px-4 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed"
                        >
                          Registration Closed
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}
